import React, { useEffect, useRef, useState } from 'react';
import { KilobaRacingGameComponent } from '../components/KilobaRacingGameComponent';
import { useNavigate } from 'react-router-dom';
import { PhaserGame } from '../phaser';
import { GameConfigService, GameConfig } from '../services/gameConfigService';
import { useAuth } from '../contexts/AuthContext';
import './KilobaRacingPage.css';
import BaseLayout from '@/components/BaseLayout';
import LeaderboardModal from '../components/LeaderboardModal';
import { GamePopup } from '@/components';

const KilobaRacingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGame, setShowGame] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  const phaserGameRef = useRef<PhaserGame | null>(null);

  // Fetch game config when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchGameConfig();
    }
  }, [isAuthenticated]);

  const fetchGameConfig = async () => {
    setIsLoading(true);
    try {
      const config = await GameConfigService.getGameConfig();
      setGameConfig(config);
    } catch (error) {
      console.error('Error fetching game config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGameEnd = (score: number) => {
    console.log('Game ended with score:', score);
    // You can add logic here to save the score, show leaderboard, etc.
  };

  const handleStartGame = () => {
    setShowGame(true);
  };

  const handleLeaderboardClick = () => {
    setShowLeaderboard(true);
  };

  const handleCloseLeaderboard = () => {
    setShowLeaderboard(false);
  };

  const handleBackClick = () => {
    // Destroy game trước khi navigate
    if (phaserGameRef.current) {
      phaserGameRef.current.destroy();
      phaserGameRef.current = null;
    }
    navigate(-1);
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="kiloba-racing-page">
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title-racing">KILOBA ĐI ĐUA</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải...</p>
        </div>
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_racing" 
        />
      </div>
    );
  }

  // Welcome screen with game instructions
  if (!showGame && gameConfig) {
    const remainingAttempts = GameConfigService.getRemainingAttempts(gameConfig, 'racing');
    const canPlay = GameConfigService.canPlayGame(gameConfig, 'racing');
    const energyReward = GameConfigService.getEnergyReward(gameConfig, 'racing');
    const requiredScore = gameConfig.racing.requiredScore;

    return (
      <div className="kiloba-racing-page">
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title-racing">KILOBA ĐI ĐUA</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        
        <div className="welcome-container">
          <div className="welcome-content">
            <div className="quiz-info">
              <h2>Chào mừng đến với Kiloba Đi Đua!</h2>
              <div className="quiz-rules">
                <p>• Nhấn vào bên trái hoặc bên phải màn hình để điều khiển Kiloba</p>
                <p>• Tránh các trướng ngại vật để đi xa nhất có thể</p>
                <p>• Cần đạt {requiredScore} điểm để nhận {energyReward} năng lượng</p>
                <p>• Giới hạn: {gameConfig.racing.dailyPlayLimit} lượt nhận thưởng/ngày</p>
              </div>
              <div className="attempts-info">
                <p>Lượt chơi còn lại: <strong>{remainingAttempts}/{gameConfig.racing.dailyPlayLimit}</strong></p>
              </div>
            </div>
            
            <button 
              className="start-quiz-button"
              onClick={handleStartGame}
              disabled={!canPlay || remainingAttempts <= 0}
            >
              {!canPlay || remainingAttempts <= 0 ? 'Hết lượt chơi' : 'Bắt đầu chơi'}
            </button>
          </div>
        </div>
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_racing" 
        />
      </div>
    );
  }

  // Game screen
  if (showGame) {
    return (
      <div className="kiloba-racing-page">
        <GamePopup />
        <div className="game-header">
          <button className="game-back-btn" onClick={handleBackClick}>
            <img src="/images/icons/back.svg" alt="Back" />
          </button>
          <h1 className="game-title-racing">KILOBA ĐI ĐUA</h1>
          <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
            <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
          </button>
        </div>
        <KilobaRacingGameComponent onGameEnd={handleGameEnd} />
        
        <LeaderboardModal 
          isOpen={showLeaderboard} 
          onClose={handleCloseLeaderboard} 
          gameType="kiloba_racing" 
        />
      </div>
    );
  }

  // Default fallback
  return (
    <div className="kiloba-racing-page">
      <div className="game-header">
        <button className="game-back-btn" onClick={handleBackClick}>
          <img src="/images/icons/back.svg" alt="Back" />
        </button>
        <h1 className="game-title-racing">KILOBA ĐI ĐUA</h1>
        <button className="leaderboard-btn" onClick={handleLeaderboardClick}>
          <img src="/images/9cloud/top_score.png" alt="Leaderboard" />
        </button>
      </div>
      
      <div className="loading-container">
        <p>Đang tải...</p>
      </div>
      
      <LeaderboardModal 
        isOpen={showLeaderboard} 
        onClose={handleCloseLeaderboard} 
        gameType="kiloba_racing" 
      />
    </div>
  );
};

const KilobaRacingPageContainer: React.FC = () => {
  return (
    <div className="viewport-wrapper">
      <BaseLayout className="common-container" backgroundColor="transparent">
        <KilobaRacingPage />
      </BaseLayout>
    </div>
  );
}

export default KilobaRacingPageContainer;
